import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Plus,
  MagnifyingGlass,
  DotsThreeVertical,
  Power,
  PencilSimple,
  Trash,
  Student as StudentIcon,
  Receipt,
  UploadSimple,
  Tag,
} from "@phosphor-icons/react";
import api, { cleanPayload, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import FeeCollectionDialog from "@/components/FeeCollectionDialog";
import StudentConcessionsDialog from "@/components/StudentConcessionsDialog";
import BulkImportStudentsDialog from "@/components/BulkImportStudentsDialog";

const EMPTY = {
  class_id: "",
  name: "",
  admission_no: "",
  roll_no: "",
  gender: "",
  dob: "",
  parent_name: "",
  parent_phone: "",
  address: "",
  fee_start_month: "",
};

function StudentDialog({ open, onOpenChange, initial, classes, sessions, schoolRule, onSubmit }) {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...EMPTY, ...initial, dob: initial.dob || "", fee_start_month: initial.fee_start_month || "" } : EMPTY);
      setError("");
    }
  }, [open, initial]);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await onSubmit(cleanPayload(form));
      onOpenChange(false);
    } catch (err) {
      setError(formatApiError(err));
    }
    setSubmitting(false);
  };

  const isEdit = Boolean(initial?.id);

  // Phase 27B — compute the default fee_start_month that would be applied
  // server-side if the cashier leaves the field blank. Used to show a
  // friendly hint ("Default: YYYY-MM · school rule: Admission month").
  //
  // Phase 27G — when school rule is `session_start`, show the active
  // session's start month immediately on dialog open (before a class is
  // picked). Once a class is selected, refine to that class's session
  // (handles the rare multi-active-session case symmetrically).
  const ruleLabel = schoolRule === "session_start" ? "Session start" : "Admission month";
  const defaultFeeStartMonth = useMemo(() => {
    const today = new Date().toISOString().slice(0, 7);
    if (schoolRule === "session_start") {
      // Prefer the class's session when a class is selected; otherwise
      // fall back to the school's active session.
      const cls = classes.find((c) => c.id === form.class_id);
      let sess = sessions.find((s) => s.id === cls?.session_id);
      if (!sess) sess = sessions.find((s) => s.is_active);
      const sd = sess?.start_date || "";
      return sd ? sd.slice(0, 7) : today;
    }
    return today;
  }, [schoolRule, classes, sessions, form.class_id]);

  const usingDefault = !form.fee_start_month || form.fee_start_month === "";
  const explicitOverride = isEdit && initial?.fee_start_month && initial.fee_start_month !== defaultFeeStartMonth;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="student-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {isEdit ? "Edit student" : "Add student"}
          </DialogTitle>
          <DialogDescription>
            Students are tied to a class and inherit its academic session. Admission numbers must be unique within a session.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label>Class *</Label>
            <Select value={form.class_id} onValueChange={(v) => setForm((f) => ({ ...f, class_id: v }))}>
              <SelectTrigger className="mt-1.5" data-testid="student-class-select">
                <SelectValue placeholder="Select class" />
              </SelectTrigger>
              <SelectContent>
                {classes.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`student-class-option-${c.id}`}>
                    {c.name}{c.section ? ` - ${c.section}` : ""}
                    <span className="text-slate-400 ml-2">· {c.session_name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Full name *</Label>
            <Input required minLength={2} value={form.name} onChange={update("name")} className="mt-1.5" data-testid="student-name-input" />
          </div>
          <div>
            <Label>Admission no. *</Label>
            <Input required value={form.admission_no} onChange={update("admission_no")} className="mt-1.5 font-mono" data-testid="student-admission-input" />
          </div>
          <div>
            <Label>Roll no.</Label>
            <Input value={form.roll_no || ""} onChange={update("roll_no")} className="mt-1.5" />
          </div>
          <div>
            <Label>Gender</Label>
            <Select value={form.gender || ""} onValueChange={(v) => setForm((f) => ({ ...f, gender: v }))}>
              <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="male">Male</SelectItem>
                <SelectItem value="female">Female</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Date of birth</Label>
            <Input type="date" value={form.dob || ""} onChange={update("dob")} className="mt-1.5" />
          </div>
          <div>
            <Label>Parent / guardian name</Label>
            <Input value={form.parent_name || ""} onChange={update("parent_name")} className="mt-1.5" />
          </div>
          <div>
            <Label>Parent phone</Label>
            <Input value={form.parent_phone || ""} onChange={update("parent_phone")} className="mt-1.5" placeholder="+91-…" data-testid="student-parent-phone-input" />
          </div>
          <div className="md:col-span-2">
            <Label>Address</Label>
            <Input value={form.address || ""} onChange={update("address")} className="mt-1.5" />
          </div>

          <div className="md:col-span-2 pt-2 border-t border-slate-100">
            <Label>
              Fee start month
              <span className="text-slate-400 font-normal ml-1">(optional)</span>
            </Label>
            <div className="mt-1.5 flex items-center gap-2 flex-wrap">
              <Input
                type="month"
                value={form.fee_start_month || ""}
                onChange={update("fee_start_month")}
                className="max-w-[180px]"
                data-testid="student-fee-start-input"
              />
              {form.fee_start_month && (
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, fee_start_month: "" }))}
                  className="text-xs text-slate-500 underline hover:text-slate-700"
                  data-testid="student-fee-start-clear"
                >
                  Clear (use school default)
                </button>
              )}
              {usingDefault ? (
                <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-medium rounded bg-slate-100 text-slate-700" data-testid="student-fee-start-badge-default">
                  Using school default · {defaultFeeStartMonth}
                </span>
              ) : (
                <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded bg-amber-100 text-amber-800" data-testid="student-fee-start-badge-override">
                  Explicit override
                </span>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-1.5">
              The month from which this student starts owing fees (YYYY-MM). Leave blank to use the school rule (<strong>{ruleLabel}</strong>). Useful for late admissions, transfer-ins, or backdated joiners.
            </p>
            {explicitOverride && (
              <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 px-2 py-1.5 rounded mt-2" data-testid="student-fee-start-existing-note">
                This student has a frozen fee-start month from when they were added. Changing it now will recalculate their pending dues.
              </p>
            )}
          </div>

          {error && (
            <div className="md:col-span-2 text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">
              {error}
            </div>
          )}

          <DialogFooter className="md:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              type="submit"
              disabled={submitting || !form.class_id}
              className="bg-blue-900 hover:bg-blue-800"
              data-testid="student-submit-btn"
            >
              {submitting ? "Saving…" : isEdit ? "Save changes" : "Add student"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Students() {
  const [students, setStudents] = useState([]);
  const [total, setTotal] = useState(0);
  const [classes, setClasses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [schoolRule, setSchoolRule] = useState("admission_month");
  const [filterClass, setFilterClass] = useState("__all__");
  const [filterSession, setFilterSession] = useState("__all__");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState({ open: false, initial: null });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [collectingFor, setCollectingFor] = useState(null);
  const [concessionsFor, setConcessionsFor] = useState(null);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);

  const activeSession = useMemo(() => sessions.find((s) => s.is_active), [sessions]);

  const filteredClasses = useMemo(() => {
    if (filterSession === "__all__") return classes;
    return classes.filter((c) => c.session_id === filterSession);
  }, [classes, filterSession]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterClass && filterClass !== "__all__") params.set("class_id", filterClass);
      if (filterSession && filterSession !== "__all__") params.set("session_id", filterSession);
      if (search) params.set("search", search);
      const url = `/school/students${params.toString() ? `?${params.toString()}` : ""}`;
      const { data } = await api.get(url);
      setStudents(data.items);
      setTotal(data.total);
    } catch (e) {
      setError(formatApiError(e));
    }
    setLoading(false);
  }, [filterClass, filterSession, search]);

  useEffect(() => {
    (async () => {
      try {
        const [c, s, me] = await Promise.all([
          api.get("/school/classes"),
          api.get("/school/sessions"),
          api.get("/school/me"),
        ]);
        setClasses(c.data);
        setSessions(s.data);
        setSchoolRule(me.data?.fee_start_rule || "admission_month");
      } catch (e) {
        setError(formatApiError(e));
      }
    })();
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  // If session changes, reset class filter if no longer valid
  useEffect(() => {
    if (filterClass !== "__all__" && filterSession !== "__all__") {
      const ok = filteredClasses.some((c) => c.id === filterClass);
      if (!ok) setFilterClass("__all__");
    }
  }, [filterClass, filterSession, filteredClasses]);

  const create = async (payload) => {
    const { data } = await api.post("/school/students", payload);
    setStudents((s) => [data, ...s]);
    setTotal((n) => n + 1);
    toast.success(`Added ${data.name}`);
  };

  const edit = async (payload) => {
    const id = dialog.initial.id;
    const { data } = await api.patch(`/school/students/${id}`, payload);
    setStudents((s) => s.map((x) => (x.id === data.id ? data : x)));
    toast.success("Student updated");
  };

  const toggle = async (s) => {
    try {
      const { data } = await api.post(`/school/students/${s.id}/toggle-status`);
      setStudents((arr) => arr.map((x) => (x.id === s.id ? data : x)));
      toast.success(`${data.name} is now ${data.is_active ? "active" : "inactive"}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/school/students/${confirmDelete.id}`);
      setStudents((s) => s.filter((x) => x.id !== confirmDelete.id));
      setTotal((n) => Math.max(0, n - 1));
      toast.success("Student deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setConfirmDelete(null);
  };

  return (
    <div className="space-y-6" data-testid="students-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">
            People
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-heading mt-1">
            Students <span className="text-slate-400 font-normal">({total})</span>
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Onboard students under a class. Admission numbers must be unique per academic session.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setBulkImportOpen(true)}
            disabled={sessions.length === 0}
            data-testid="bulk-import-btn"
            className="gap-2"
          >
            <UploadSimple size={16} weight="bold" /> Import CSV
          </Button>
          <Button
            onClick={() => setDialog({ open: true, initial: null })}
            disabled={classes.length === 0}
            data-testid="add-student-btn"
            className="bg-blue-900 hover:bg-blue-800 gap-2"
          >
            <Plus size={16} weight="bold" /> Add student
          </Button>
        </div>
      </div>

      {classes.length === 0 && (
        <div className="p-4 border border-amber-200 bg-amber-50 text-amber-800 rounded-md text-sm" data-testid="no-classes-warning">
          You need at least one class before adding students. Go to <strong>Classes</strong> and create one.
        </div>
      )}

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">
          {error}
        </div>
      )}

      <div className="border border-slate-200 bg-white rounded-lg">
        <div className="p-4 border-b border-slate-200 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 max-w-sm min-w-[200px]">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search name, admission no., parent…"
              className="pl-9 h-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="students-search-input"
            />
          </div>
          <Select value={filterSession} onValueChange={setFilterSession}>
            <SelectTrigger className="w-44 h-9" data-testid="students-filter-session">
              <SelectValue placeholder="All sessions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All sessions</SelectItem>
              {sessions.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterClass} onValueChange={setFilterClass}>
            <SelectTrigger className="w-52 h-9" data-testid="students-filter-class">
              <SelectValue placeholder="All classes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All classes</SelectItem>
              {filteredClasses.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}{c.section ? ` - ${c.section}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="overflow-x-auto hidden md:block">
          <table className="w-full text-sm" data-testid="students-table">
            <thead className="bg-slate-50">
              <tr className="text-[11px] uppercase tracking-wider text-slate-500">
                <th className="text-left font-semibold px-4 py-3">Student</th>
                <th className="text-left font-semibold px-4 py-3">Adm. no.</th>
                <th className="text-left font-semibold px-4 py-3">Class</th>
                <th className="text-left font-semibold px-4 py-3">Parent</th>
                <th className="text-left font-semibold px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && students.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>
              ) : students.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-12 py-12 text-center" data-testid="students-empty">
                    <StudentIcon size={32} className="mx-auto text-slate-300 mb-2" />
                    <div className="text-sm text-slate-500">
                      {classes.length === 0
                        ? "No students yet. Create a class first."
                        : "No students match your filters."}
                    </div>
                  </td>
                </tr>
              ) : (
                students.map((s) => (
                  <tr key={s.id} className="hover:bg-slate-50 transition-colors" data-testid={`student-row-${s.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-semibold text-slate-700">
                          {(s.name || "?").charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-slate-900 truncate">{s.name}</div>
                          {s.roll_no && <div className="text-xs text-slate-500">Roll {s.roll_no}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-700">{s.admission_no}</td>
                    <td className="px-4 py-3 text-slate-700">
                      <div className="text-sm">{s.class_name || "—"}</div>
                      <div className="text-xs text-slate-500">{s.session_name}</div>
                      {s.fee_start_month && (
                        <div
                          className="text-[10px] text-slate-500 mt-0.5 font-mono"
                          title="Fee liability start month"
                          data-testid={`student-fee-start-${s.id}`}
                        >
                          Fees from {s.fee_start_month}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      <div className="text-sm">{s.parent_name || "—"}</div>
                      <div className="text-xs text-slate-500 font-mono">{s.parent_phone || ""}</div>
                    </td>
                    <td className="px-4 py-3">
                      {s.is_active ? (
                        <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded bg-emerald-100 text-emerald-700">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded bg-slate-200 text-slate-700">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setCollectingFor(s.id)}
                          disabled={!s.is_active}
                          className="gap-1.5 h-8 text-xs"
                          data-testid={`collect-fees-${s.id}`}
                        >
                          <Receipt size={12} /> Collect
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              className="p-1.5 rounded hover:bg-slate-100 text-slate-600"
                              data-testid={`student-actions-${s.id}`}
                            >
                              <DotsThreeVertical size={18} weight="bold" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setDialog({ open: true, initial: s })} data-testid={`edit-student-${s.id}`}>
                              <PencilSimple size={14} className="mr-2" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setConcessionsFor(s)} data-testid={`concessions-student-${s.id}`}>
                              <Tag size={14} className="mr-2" /> Concessions
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => toggle(s)} data-testid={`toggle-student-${s.id}`}>
                              <Power size={14} className="mr-2" />
                              {s.is_active ? "Deactivate" : "Activate"}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setConfirmDelete(s)} data-testid={`delete-student-${s.id}`}>
                              <Trash size={14} className="mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Phase 27I.4 — mobile card list (visible <md only). Same data, same
            handlers — pure presentational alternative for narrow screens. */}
        <div className="md:hidden divide-y divide-slate-100" data-testid="students-mobile-list">
          {loading && students.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-slate-500">Loading…</div>
          ) : students.length === 0 ? (
            <div className="px-6 py-12 text-center" data-testid="students-empty-mobile">
              <StudentIcon size={32} className="mx-auto text-slate-300 mb-2" />
              <div className="text-sm text-slate-500">
                {classes.length === 0
                  ? "No students yet. Create a class first."
                  : "No students match your filters."}
              </div>
            </div>
          ) : (
            students.map((s) => (
              <div key={s.id} className="p-4" data-testid={`student-card-${s.id}`}>
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center text-sm font-semibold text-slate-700 shrink-0">
                    {(s.name || "?").charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-slate-900 truncate">{s.name}</div>
                      {s.is_active ? (
                        <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold rounded bg-emerald-100 text-emerald-700 shrink-0">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold rounded bg-slate-200 text-slate-700 shrink-0">
                          Inactive
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      <span className="font-mono">{s.admission_no}</span>
                      {s.class_name && <> · {s.class_name}</>}
                    </div>
                    {s.parent_phone && (
                      <div className="text-xs text-slate-500 font-mono truncate mt-0.5">{s.parent_phone}</div>
                    )}
                    {s.fee_start_month && (
                      <div className="text-[10px] text-slate-500 mt-0.5 font-mono">
                        Fees from {s.fee_start_month}
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setCollectingFor(s.id)}
                    disabled={!s.is_active}
                    className="flex-1 h-11 gap-1.5"
                    data-testid={`collect-fees-mobile-${s.id}`}
                  >
                    <Receipt size={14} /> Collect
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="h-11 w-11 rounded border border-slate-200 hover:bg-slate-50 text-slate-600 flex items-center justify-center"
                        data-testid={`student-actions-mobile-${s.id}`}
                      >
                        <DotsThreeVertical size={20} weight="bold" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setDialog({ open: true, initial: s })} data-testid={`edit-student-mobile-${s.id}`}>
                        <PencilSimple size={14} className="mr-2" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setConcessionsFor(s)} data-testid={`concessions-student-mobile-${s.id}`}>
                        <Tag size={14} className="mr-2" /> Concessions
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => toggle(s)} data-testid={`toggle-student-mobile-${s.id}`}>
                        <Power size={14} className="mr-2" />
                        {s.is_active ? "Deactivate" : "Activate"}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setConfirmDelete(s)} data-testid={`delete-student-mobile-${s.id}`}>
                        <Trash size={14} className="mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <StudentDialog
        open={dialog.open}
        onOpenChange={(v) => setDialog({ open: v, initial: v ? dialog.initial : null })}
        initial={dialog.initial}
        classes={classes}
        sessions={sessions}
        schoolRule={schoolRule}
        onSubmit={dialog.initial ? edit : create}
      />

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete student?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes <strong>{confirmDelete?.name}</strong>. Once fee payments are recorded, deletion will be blocked. Use deactivate instead if you just want to disable access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-student">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <FeeCollectionDialog
        open={!!collectingFor}
        onOpenChange={(v) => { if (!v) setCollectingFor(null); }}
        studentId={collectingFor}
      />

      <StudentConcessionsDialog
        open={!!concessionsFor}
        onOpenChange={(v) => { if (!v) setConcessionsFor(null); }}
        student={concessionsFor}
      />

      <BulkImportStudentsDialog
        open={bulkImportOpen}
        onOpenChange={setBulkImportOpen}
        sessions={sessions}
        defaultSessionId={
          filterSession !== "__all__" ? filterSession : activeSession?.id || ""
        }
        onImported={() => load()}
      />
    </div>
  );
}
